import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { useAuth } from '../contexts/AuthContext';

const ROLE_OPTIONS = [
  {
    value: 'investor',
    label: 'Investor',
    icon: TrendingUpIcon,
    description: 'I invest in energy assets',
  },
  {
    value: 'asset_manager',
    label: 'Asset Manager',
    icon: AccountBalanceIcon,
    description: 'I manage energy assets',
  },
  {
    value: 'both',
    label: 'Both',
    icon: AllInclusiveIcon,
    description: 'I invest and manage assets',
  },
];

const ProfileSetup = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { updateUserProfile } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName || !lastName || !companyName || !role) {
      setError('Please fill in all fields and select your role');
      return;
    }

    try {
      setLoading(true);
      await updateUserProfile({
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
        role,
      });
      navigate('/portfolios');
    } catch (err) {
      setError('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #D98A2B, #E9B065, #D98A2B)',
          backgroundSize: '200% 100%',
          animation: 'gradientShift 4s ease infinite',
        },
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(217, 138, 43, 0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 480,
          mx: 2,
          p: 5,
          bgcolor: 'background.paper',
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, rgba(217, 138, 43, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 28, color: '#D98A2B' }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: 'text.primary',
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            Welcome to Lumeros
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            Complete your profile to get started
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{ mb: 3, borderRadius: '10px' }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
          <TextField
            fullWidth
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoFocus
            size="medium"
          />
          <TextField
            fullWidth
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            size="medium"
          />
        </Box>

        <TextField
          fullWidth
          label="Company name"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          sx={{ mb: 3.5 }}
          size="medium"
        />

        {/* Role Selection */}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.85rem',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 1.5,
          }}
        >
          Your role
        </Typography>

        <ToggleButtonGroup
          value={role}
          exclusive
          onChange={(e, newRole) => {
            if (newRole !== null) setRole(newRole);
          }}
          sx={{
            width: '100%',
            display: 'flex',
            gap: 1.5,
            mb: 4,
            '& .MuiToggleButtonGroup-grouped': {
              border: '1px solid rgba(255, 255, 255, 0.08) !important',
              borderRadius: '12px !important',
              m: '0 !important',
            },
          }}
        >
          {ROLE_OPTIONS.map(({ value, label, icon: Icon, description }) => (
            <ToggleButton
              key={value}
              value={value}
              sx={{
                flex: 1,
                flexDirection: 'column',
                py: 2,
                px: 1.5,
                textTransform: 'none',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: 'rgba(217, 138, 43, 0.08)',
                  borderColor: '#D98A2B !important',
                  '&:hover': {
                    bgcolor: 'rgba(217, 138, 43, 0.12)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.02)',
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 24,
                  mb: 0.5,
                  color: role === value ? '#D98A2B' : 'text.secondary',
                  transition: 'color 0.2s',
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  color: role === value ? '#D98A2B' : 'text.primary',
                  mb: 0.3,
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  lineHeight: 1.3,
                }}
              >
                {description}
              </Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            borderRadius: '10px',
            bgcolor: '#D98A2B',
            boxShadow: '0 2px 8px rgba(217, 138, 43, 0.3)',
            '&:hover': {
              bgcolor: '#BE7521',
              boxShadow: '0 4px 12px rgba(217, 138, 43, 0.4)',
            },
            '&:disabled': {
              bgcolor: 'rgba(217, 138, 43, 0.5)',
              color: '#F4F2EC',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: '#F4F2EC' }} />
          ) : (
            'Continue'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileSetup;
